import {
  Autocomplete, Box, Button, CircularProgress, FormControl, Grid, IconButton, InputLabel,
  MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography, Collapse,
  TableSortLabel
} from "@mui/material";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useEffect, useState, Fragment } from "react";
import { DailyReport } from "../types/dailyReportTypes";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Project } from "../types/projectTypes";
import { Employee } from "../types/EmployeeTypes";
import { deleteDailyReport, getDailyReportDetails, updateDailyReport } from "../api/dailyReportApi";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import ColumnFilterMenu from "./ColumnFilterMenu";
import { exportRowsToExcel, exportSheetsToExcel, safeFileNamePart, todayStamp } from "../utils/exportExcel";
import { buildDailyReportSheets, hasNoDetailRows } from "../utils/dailyReportExport";
import { User } from "../types/userTypes";
import { getUsers } from "../api/userApi";
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from "../contexts/SnackbarContext";

/** Estados posibles de un reporte, derivados de isDraft. */
const STATUS_DRAFT = 'Borrador';
const STATUS_FINAL = 'Finalizado';

/** Columnas del listado que admiten filtro por valores. */
type ColumnKey = 'status' | 'controller' | 'project';

interface dailyReportProps {
  reports: DailyReport[];
  projects: Project[];       // Lista completa (para mostrar nombres en filas)
  activeProjects?: Project[]; // NUEVO: Lista filtrada (para el ComboBox de filtro)
  employees: Employee[];
  onFilter: (filteredData: DailyReport[]) => void;
  onSelectReport: (report: DailyReport | null) => void;
  selectedReport: DailyReport | null;
  detailContent: React.ReactNode;
}

const DailyReportTable: React.FC<dailyReportProps> = ({
    reports,
    projects,
    activeProjects = [], // Default vacío por seguridad
    employees,
    onFilter,
    onSelectReport,
    selectedReport,
    detailContent
}) => {
  const [dailyReports, setDailyReport] = useState<DailyReport[]>([]);
  const [filteredReports, setFilteredReports] = useState<DailyReport[]>([]);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', project: '', controller: '', status: '' });
  const [editingReportId, setEditingReportId] = useState<number | null>(null);
  const [editedReportData, setEditedReportData] = useState<any>({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [dailyReportId, setDailyReportId] = useState(0);
  const [dailyReportName, setDailyReportName] = useState('');
  const [userRoleDescription, setUserRoleDescription] = useState<string | null>(null);
  const [user, setUser] = useState<User[]>([]);

  // NUEVO ESTADO PARA EL ORDEN
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Filtros por columna (se aplican en vivo sobre el resultado de la barra superior).
  const [columnFilters, setColumnFilters] = useState<Record<ColumnKey, string[]>>({
    status: [],
    controller: [],
    project: [],
  });
  const [filterMenu, setFilterMenu] = useState<{ anchorEl: null | HTMLElement; columnKey: ColumnKey | null }>({
    anchorEl: null,
    columnKey: null,
  });

  // Reporte cuyo detalle se está descargando, para mostrar el avance en su fila.
  const [exportingReportId, setExportingReportId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedData = JSON.parse(userData);
      const roleDescription = parsedData?.rols?.description || null;
      setUserRoleDescription(roleDescription);
    }
  }, []);

  useEffect(() => {
    setFilteredReports(reports);
    fetchUsers();
  }, [reports]);

  const fetchUsers = async () => {
    try {
        const users = await getUsers();
        setUser(users);
    } catch (error) {
        console.error("Error cargando usuarios", error);
    }
  }

  const getCreatorName = (identificationNum: string): string => {
    if (!identificationNum) return "Sin ID";
    const cleanId = identificationNum.replace(/\./g, '').replace(/-/g, '').toLowerCase();
    if (employees && employees.length > 0) {
        const employee = employees.find(e => {
            const empRut = (e.rut || "").replace(/\./g, '').replace(/-/g, '').toLowerCase();
            return empRut === cleanId;
        });
        if (employee) {
            return (employee.firstName && employee.lastName)
                ? `${employee.firstName} ${employee.lastName}`
                : (employee.name || identificationNum);
        }
    }
    if (user && user.length > 0) {
        const foundUser = user.find(u => {
            const userRut = (u.identificationNum || "").replace(/\./g, '').replace(/-/g, '').toLowerCase();
            return userRut === cleanId;
        });
        if (foundUser) {
            return foundUser.name || identificationNum;
        }
    }
    return identificationNum;
  };

  const handleFilterChange = (typeFilter: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = typeFilter.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const applyFilters = () => {
    const filteredData = reports.filter((report) => {
      const reportDate = new Date(report.date);
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;
      const isInRange = (!startDate || reportDate >= startDate) && (!endDate || reportDate <= endDate);
      const matchesProject = filters.project ? report.idProject === Number(filters.project) : true;
      const matchesController = filters.controller ? report.identificationNum === filters.controller : true;
      const matchesStatus = filters.status ? getStatusLabel(report) === filters.status : true;
      return isInRange && matchesProject && matchesController && matchesStatus;
    });
    setFilteredReports(filteredData);
  };

  const clearFilters = () => {
    setFilters({ startDate: '', endDate: '', project: '', controller: '', status: '' });
    setColumnFilters({ status: [], controller: [], project: [] });
    setFilteredReports(reports);
  };

  const handleProjectChange = (e: SelectChangeEvent) => {
    const { value } = e.target;
    setFilters({
      ...filters,
      project: value as string,
    });
  };

  const uniqueCreatorsRuts = Array.from(new Set(reports.map(r => r.identificationNum)));

  const handleOpenConfirmModal = (id: number, name: any) => {
    setDailyReportId(id);
    setDailyReportName(name);
    setIsConfirmModalOpen(true)
  }

  const handleEditClick = (report: DailyReport) => {
    if (report.id) {
      navigate(`/reporte-diario/editar/${report.id}`);
    }
  };

  const handleCancelClick = () => {
    setEditingReportId(null);
    setEditedReportData({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedReportData({
      ...editedReportData,
      [name]: value,
    });
  };

  const handleSaveClick = async () => {
    try {
      await updateDailyReport(editedReportData.id, { dailyReport: editedReportData });
      const updatedReports = filteredReports.map((report) =>
        report.id === editedReportData.id ? { ...report, ...editedReportData } : report
      );
      setFilteredReports(updatedReports);
      setDailyReport(updatedReports);
      setEditingReportId(null);
      setEditedReportData({});
      showMessage("Reporte actualizado correctamente", "success");
    } catch (error: any) {
      console.error('Error al actualizar el reporte diario:', error);
      let mensajeError = "No se pudo guardar los cambios.";
      if (error.response) {
          const data = error.response.data;
          if (data?.message) mensajeError = data.message;
          else if (typeof data === 'string') mensajeError = data;
          else if (data?.errors && Array.isArray(data.errors)) mensajeError = data.errors.map((e: any) => e.defaultMessage || e).join(", ");
      }
      showMessage(mensajeError, "error");
    }
  };

  const handleDeleteClick = async (reportId: number) => {
    try {
      await deleteDailyReport(reportId);
      const updatedReports = filteredReports.filter((report) => report.id !== reportId);
      setFilteredReports(updatedReports);
      setDailyReport(updatedReports);
      if (reportId === editingReportId) {
        onSelectReport(null);
      }
      showMessage("Reporte eliminado correctamente", "success");
    } catch (error) {
      console.error('Error al eliminar el reporte diario:', error);
      showMessage("Error al eliminar el reporte", "error");
    }
    onSelectReport(null);
  };

  const getProjectNameById = (idProject: number) => {
    const project = projects.find((proj) => proj.id === idProject);
    return project ? project.name : 'Proyecto no encontrado';
  };

  /**
   * Mismo nombre, pero garantizado como texto: los filtros y el export necesitan
   * string. No se usa en las celdas para no cambiar lo que ya se mostraba.
   */
  const projectNameText = (idProject: number): string => getProjectNameById(idProject) ?? '';

  /** Estado del reporte tal como se muestra en la columna Estado. */
  const getStatusLabel = (report: DailyReport) => (report.isDraft ? STATUS_DRAFT : STATUS_FINAL);

  const handleToggleDetail = (report: DailyReport) => {
      if (selectedReport && selectedReport.id === report.id) {
        onSelectReport(null);
      } else {
        onSelectReport(report);
      }
  };

  // Los filtros por columna se aplican en vivo sobre lo que dejó la barra superior.
  const columnFilteredReports = filteredReports.filter((report) => (
    (columnFilters.status.length === 0 || columnFilters.status.includes(getStatusLabel(report))) &&
    (columnFilters.controller.length === 0 || columnFilters.controller.includes(getCreatorName(report.identificationNum))) &&
    (columnFilters.project.length === 0 || columnFilters.project.includes(projectNameText(report.idProject)))
  ));

  const sortedFilteredReports = [...columnFilteredReports].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  /** Valores únicos de la columna cuyo menú está abierto (sobre el universo completo). */
  const uniqueValues: string[] = (() => {
    switch (filterMenu.columnKey) {
      case 'status':
        return [STATUS_DRAFT, STATUS_FINAL];
      case 'controller':
        return Array.from(new Set(reports.map(r => getCreatorName(r.identificationNum)))).filter(Boolean).sort();
      case 'project':
        return Array.from(new Set(reports.map(r => projectNameText(r.idProject)))).filter(Boolean).sort();
      default:
        return [];
    }
  })();

  const handleColumnFilterChange = (selected: string[]) => {
    if (!filterMenu.columnKey) return;
    setColumnFilters(prev => ({ ...prev, [filterMenu.columnKey as ColumnKey]: selected }));
  };

  /** Exporta el listado tal como se está viendo, respetando los filtros activos. */
  const handleExportExcel = () => {
    const rows = sortedFilteredReports.map((report) => ({
      'Fecha Reporte': report.date ? new Date(report.date).toLocaleDateString() : '',
      'Estado': getStatusLabel(report),
      'Control de Obras': getCreatorName(report.identificationNum),
      'Proyecto': projectNameText(report.idProject),
      'Frente de Trabajo': report.workFront ?? '',
      'Total HH': report.totalHh ?? '',
      'Total HM': report.totalHm ?? '',
      'Observaciones': report.observations || '',
    }));

    if (!exportRowsToExcel(rows, 'Reportes Diarios', `Reportes_Diarios_${todayStamp()}.xlsx`)) {
      showMessage('No hay reportes para exportar.', 'info');
    }
  };

  /** Fecha del reporte como YYYY-MM-DD, para el nombre del archivo. */
  const reportDateStamp = (report: DailyReport): string => {
    if (!report.date) return todayStamp();
    const date = new Date(report.date);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  };

  /**
   * Exporta un reporte con su detalle: una hoja de resumen más personal, equipos,
   * producción y subcontratos de cada frente de trabajo.
   *
   * El detalle no viene en el listado, así que se pide al API al momento de exportar.
   */
  const handleExportReportDetail = async (report: DailyReport) => {
    if (!report.id) return;
    setExportingReportId(report.id);
    try {
      const details = await getDailyReportDetails(report.id);
      // El API responde un arreglo con una entrada por actividad (a veces un objeto suelto).
      const detailList: any[] = Array.isArray(details) ? details : [details];

      const projectName = projectNameText(report.idProject);

      const sheets = buildDailyReportSheets({
        dateLabel: report.date ? new Date(report.date).toLocaleDateString() : '',
        statusLabel: getStatusLabel(report),
        projectName,
        controllerName: getCreatorName(report.identificationNum),
        totalHh: report.totalHh,
        totalHm: report.totalHm,
        observations: report.observations,
      }, detailList);

      if (hasNoDetailRows(sheets)) {
        showMessage('Este reporte no tiene detalle registrado.', 'info');
        return;
      }

      exportSheetsToExcel(sheets, `Reporte_${safeFileNamePart(projectName, 'Proyecto')}_${reportDateStamp(report)}.xlsx`);

    } catch (error) {
      console.error('Error al exportar el detalle del reporte diario:', error);
      showMessage('No se pudo obtener el detalle del reporte para exportar.', 'error');
    } finally {
      setExportingReportId(null);
    }
  };

  /**
   * Cabecera con icono de filtro por columna.
   *
   * Es una función que devuelve JSX, no un componente declarado aquí dentro: un
   * componente definido en el render cambia de identidad en cada pasada, React
   * remontaría la celda y el anchorEl guardado apuntaría a un nodo ya desprendido,
   * dejando el menú mal posicionado.
   */
  const renderFilterableHeader = (label: string, columnKey: ColumnKey, align?: 'left' | 'center') => (
    <TableCell key={columnKey} align={align} sx={{ fontWeight: 'bold', color: '#004F86' }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {label}
        <IconButton
          size="small"
          title={`Filtrar por ${label}`}
          onClick={(e) => setFilterMenu({ anchorEl: e.currentTarget, columnKey })}
          sx={{ color: columnFilters[columnKey].length > 0 ? '#004F86' : 'inherit' }}
        >
          <FilterListIcon fontSize="small" />
        </IconButton>
      </Box>
    </TableCell>
  );

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={1.5}>
          <TextField
            label="Fecha Inicio"
            name="startDate"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.startDate}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={1.5}>
          <TextField
            label="Fecha Fin"
            name="endDate"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.endDate}
            onChange={handleFilterChange}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <FormControl fullWidth>
            <InputLabel>Proyecto</InputLabel>
            <Select
              label="Proyecto"
              name="project"
              value={filters.project}
              onChange={handleProjectChange}
            >
              {(activeProjects.length > 0 ? activeProjects : projects).map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2.5}>
          <Autocomplete
            options={uniqueCreatorsRuts}
            getOptionLabel={(rut) => getCreatorName(rut)}
            value={filters.controller || null}
            onChange={(_, newValue) => {
              setFilters({
                ...filters,
                controller: newValue || '',
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Controlador de Obras"
                fullWidth
              />
            )}
            noOptionsText="Sin opciones"
          />
        </Grid>
        <Grid item xs={12} sm={2}>
          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              name="status"
              value={filters.status}
              onChange={(e: SelectChangeEvent) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value={STATUS_DRAFT}>{STATUS_DRAFT}</MenuItem>
              <MenuItem value={STATUS_FINAL}>{STATUS_FINAL}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2} container justifyContent="space-between">
          <Button variant="contained" color="primary" onClick={applyFilters} style={{ marginTop: '10px' }}>
            Filtrar
          </Button>

          <Button variant="contained" onClick={clearFilters} style={{ marginTop: '10px' }}>
            Limpiar filtros
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          {sortedFilteredReports.length === reports.length
            ? `${reports.length} reportes`
            : `${sortedFilteredReports.length} de ${reports.length} reportes`}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleExportExcel}
        >
          Exportar Excel
        </Button>
      </Box>

      <TableContainer component={Paper} style={{ marginTop: '10px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>
                <TableSortLabel
                  active={true}
                  direction={sortOrder}
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                >
                  Fecha Reporte
                </TableSortLabel>
              </TableCell>

              {renderFilterableHeader('Estado', 'status', 'left')}
              {renderFilterableHeader('Control de Obras', 'controller')}
              {renderFilterableHeader('Proyecto', 'project')}
              <TableCell sx={{ fontWeight: 'bold', color: '#004F86' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* MODIFICADO: Usar el arreglo ordenado en lugar de filteredReports */}
            {sortedFilteredReports.map((report) => (
              <Fragment key={report.id}>
                <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                <TableCell>{report.date ? new Date(report.date).toLocaleDateString() : 'Fecha no disponible'}</TableCell>

                <TableCell>
                  {report.isDraft ? (
                    <span style={{ backgroundColor: '#fff7e6', color: '#fa8c16', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ffd591', fontWeight: 'bold', fontSize: '12px', display: 'inline-block', whiteSpace: 'nowrap' }}>
                        BORRADOR ✏️
                    </span>
                  ) : (
                    <span style={{ backgroundColor: '#e3f2fd', color: '#0277bd', padding: '4px 8px', borderRadius: '4px', border: '1px solid #90caf9', fontWeight: 'bold', fontSize: '11px', display: 'inline-block', whiteSpace: 'nowrap' }}>
                        FINALIZADO ✅
                    </span>
                  )}
                </TableCell>

                <TableCell>
                  {editingReportId === report.id ? (
                    <Autocomplete
                      options={employees.filter((employee) => employee.position === 'Administrativo de Obra')}
                      getOptionLabel={(option) => option.name || ''}
                      value={employees.find((emp) => emp.rut === editedReportData.controller) || null}
                      onChange={(event, newValue) => {
                        setEditedReportData({
                          ...editedReportData,
                          identificationNum: newValue ? newValue.rut : '',
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Controlador de Obras"
                          fullWidth
                        />
                      )}
                    />
                  ) : (
                    getCreatorName(report.identificationNum)
                  )}
                </TableCell>
                <TableCell>
                  {editingReportId === report.id ? (
                    <Typography>{getProjectNameById(report.idProject)}</Typography>
                  ) : (
                    getProjectNameById(report.idProject)
                  )}
                </TableCell>
                <TableCell>
                  {editingReportId === report.id ? (
                    <>
                      <IconButton onClick={handleSaveClick}>
                        <SaveIcon color="primary" />
                      </IconButton>
                      <IconButton onClick={handleCancelClick} color='primary'>
                        Cancelar
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton onClick={() => handleEditClick(report)}>
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton aria-label="expand row" size="small" onClick={() => handleToggleDetail(report)}>
                        {selectedReport?.id === report.id ? <KeyboardArrowUpIcon /> : <VisibilityIcon color="primary" />}
                      </IconButton>
                      {exportingReportId === report.id ? (
                        <CircularProgress size={20} sx={{ mx: 1.5, verticalAlign: 'middle' }} />
                      ) : (
                        <IconButton
                          title="Exportar este reporte con su detalle"
                          onClick={() => handleExportReportDetail(report)}
                          disabled={exportingReportId !== null}
                        >
                          <FileDownloadOutlinedIcon color="primary" />
                        </IconButton>
                      )}
                      {userRoleDescription !== 'Control de Obras' && (
                        <IconButton onClick={() => handleOpenConfirmModal(report.id || 0, report.date)}>
                          <DeleteIcon color="primary" />
                        </IconButton>
                      )}
                    </>
                  )}
                </TableCell>
              </TableRow>
               <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                 <Collapse in={selectedReport?.id === report.id} timeout="auto" unmountOnExit>
                  <Box sx={{ margin: 1, padding: 2, backgroundColor: '#f9f9f9' }}>
                 {detailContent}
                  </Box>
                 </Collapse>
                </TableCell>
               </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ColumnFilterMenu
        anchorEl={filterMenu.anchorEl}
        values={uniqueValues}
        selected={filterMenu.columnKey ? columnFilters[filterMenu.columnKey] : []}
        onClose={() => setFilterMenu({ anchorEl: null, columnKey: null })}
        onChange={handleColumnFilterChange}
      />

      {userRoleDescription !== 'Control de Obras' && (
        <ConfirmDeleteModal
          open={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onSubmit={handleDeleteClick}
          id={dailyReportId}
          name={dailyReportName}
          title={'DAILYREPORT'}
        />
      )}
    </Box>
  );
}

export default DailyReportTable;